import React from 'react';
import { Play } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';

interface YouTubeEmbedProps {
  url: string;
  title: string;
  className?: string;
  showThumbnail?: boolean;
}

// Extract YouTube video ID from various URL formats
function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

// Generate YouTube embed URL
function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`;
}

// Generate YouTube thumbnail URL
function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

export const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({ 
  url, 
  title, 
  className = "",
  showThumbnail = false 
}) => {
  const videoId = extractYouTubeVideoId(url);

  if (!videoId) {
    return (
      <div className={`border border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-500 ${className}`}>
        <p className="text-sm">Invalid YouTube URL</p>
      </div>
    );
  }

  const embedUrl = getYouTubeEmbedUrl(videoId);
  const thumbnailUrl = getYouTubeThumbnailUrl(videoId);

  if (showThumbnail) {
    return (
      <div className={`relative group cursor-pointer ${className}`}>
        <AspectRatio ratio={16 / 9}>
          <img
            src={thumbnailUrl}
            alt={title}
            className="w-full h-full object-cover rounded-lg"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex items-center justify-center group-hover:bg-opacity-50 transition-colors">
            <div className="bg-red-600 rounded-full p-3 group-hover:bg-red-700 transition-colors">
              <Play className="w-6 h-6 text-white fill-current" />
            </div>
          </div>
        </AspectRatio>
      </div>
    );
  }

  return (
    <div className={className}>
      <AspectRatio ratio={16 / 9}>
        <iframe
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full rounded-lg border-0"
        />
      </AspectRatio>
    </div>
  );
};

export default YouTubeEmbed;